// client/src/coms/e2ee/DeviceVerifyHost.js
// "Verify this browser": opened from an encrypted message this device cannot read, or from the encryption line of a
// channel or DM (requestDeviceVerification in cryptoSession.js), so nobody has to hunt through settings. It only
// opens when asked: there are no automatic prompts. With the recovery key the browser gets the account's identity,
// signs itself and restores the encrypted history the key backup holds. Nothing changes if the key is wrong.
import React, { useEffect, useState } from 'react';
import { comsApiGet } from '../comsApi';
import { accountEncryptionStatus, cryptoAvailable, deviceTrust, hasLocalDevice, onDeviceVerificationRequest, restoreWithRecoveryKey } from './cryptoSession';
import './e2ee.css';

function openPrivacySettings() {
  try {
    window.__voxPendingSettingsTab = 'privacy';
    window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'privacy' } }));
  } catch {}
}

const ERRORS = {
  recovery_key_wrong: 'That recovery key is not correct. Nothing was changed on this browser.',
  recovery_missing: 'Your account has no recovery key set up, so this browser cannot be verified with one.',
  recovery_damaged: 'Your stored encryption secrets could not be read. Nothing was changed on this browser.',
};

export default function DeviceVerifyHost() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('loading'); // loading | unsupported | none | verified | enter | working | done
  const [mx, setMx] = useState('');
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(
    () =>
      onDeviceVerificationRequest(async () => {
        setOpen(true);
        setStep('loading');
        setError('');
        setInput('');
        setResult(null);
        if (!cryptoAvailable()) return setStep('unsupported');
        try {
          const pref = await comsApiGet('/e2ee/preference');
          const who = String((pref && pref.user_id_mx) || '');
          setMx(who);
          // Looks at the account first, so just opening this dialog never creates a device on this browser.
          const acct = await accountEncryptionStatus();
          if (!acct.has_identity) return setStep('none');
          if (hasLocalDevice(who) && (await deviceTrust(who)).deviceSigned) return setStep('verified');
          setStep(acct.has_recovery ? 'enter' : 'none');
        } catch (e) {
          setError(String((e && e.message) || e));
          setStep('enter');
        }
      }),
    []
  );

  if (!open) return null;
  const close = () => setOpen(false);

  const verify = async () => {
    setStep('working');
    setError('');
    try {
      const r = await restoreWithRecoveryKey(mx, input);
      setInput('');
      setResult(r || {});
      setStep('done');
    } catch (e) {
      setError(ERRORS[e && e.code] || `This browser was not verified: ${String((e && e.message) || e)}`);
      setStep('enter');
    }
  };

  return (
    <div className="vx-e2ee-modal-backdrop" role="presentation" onClick={(e) => e.target === e.currentTarget && step !== 'working' && close()}>
      <div className="vx-e2ee-modal" role="dialog" aria-modal="true" aria-label="Verify this browser" data-testid="e2ee-verify-dialog">
        <h3 className="vx-e2ee-modal-title">Verify this browser</h3>
        {step === 'loading' ? <p>Checking this browser’s encryption…</p> : null}
        {step === 'unsupported' ? <p>This browser cannot run end-to-end encryption (it needs WebAssembly and IndexedDB), so it cannot read encrypted messages.</p> : null}
        {step === 'none' ? (
          <>
            <p>Your account does not use end-to-end encryption yet, so there is nothing to verify.</p>
            <p>Encryption is set up when you turn on encrypted direct messages in Settings → Privacy &amp; Security, or accept an encrypted server.</p>
          </>
        ) : null}
        {step === 'verified' ? <p data-testid="e2ee-verify-already">✅ This browser is already verified. Messages it still cannot read were not shared with it, for example because they were sent before you joined or before any of your verified devices received them.</p> : null}
        {step === 'enter' || step === 'working' ? (
          <>
            <p>Encrypted messages can only be read on browsers and devices you have verified. Enter the recovery key you saved when you set up encryption.</p>
            <p className="vx-e2ee-modal-sub">
              Your recovery key never leaves this browser. Verifying also restores the encrypted history your key backup holds. Messages sent before you joined, or
              that none of your verified devices received, stay unreadable.
            </p>
            <input
              className="vx-e2ee-input"
              type="password"
              autoComplete="off"
              spellCheck={false}
              placeholder="Recovery key"
              value={input}
              disabled={step === 'working'}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && input.trim() && verify()}
              data-testid="e2ee-verify-input"
            />
          </>
        ) : null}
        {step === 'done' && result ? (
          <div className="vx-e2ee-explain" data-testid="e2ee-verify-done">
            <strong>✅ This browser is verified.</strong>
            <span>
              {result.restored ? `${result.restored} message key${result.restored === 1 ? '' : 's'} restored from your key backup.` : 'Your key backup had no message keys to restore.'}
              {result.skipped ? ` ${result.skipped} damaged backup entr${result.skipped === 1 ? 'y was' : 'ies were'} skipped.` : ''}
            </span>
            <span>
              Restored messages show “sender not confirmed” until the sender’s device shares that conversation key with this browser directly (usually with their
              next message).
            </span>
            {result.fingerprint ? (
              <span>
                Your identity safety code: <code data-testid="e2ee-verify-code">{result.fingerprint}</code>. It should match the code shown under Settings → Privacy &amp; Security on
                your other devices.
              </span>
            ) : null}
          </div>
        ) : null}
        {error ? (
          <div className="vx-acct-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="vx-e2ee-confirm-row">
          {step === 'enter' || step === 'working' ? (
            <button type="button" className="vx-btn small" disabled={step === 'working' || !input.trim()} onClick={verify} data-testid="e2ee-verify-submit">
              {step === 'working' ? 'Verifying…' : 'Verify this browser'}
            </button>
          ) : null}
          {step === 'enter' || step === 'none' ? (
            <button
              type="button"
              className="vx-btn small"
              onClick={() => {
                close();
                openPrivacySettings();
              }}
            >
              {step === 'none' ? 'Encryption settings' : 'I lost my recovery key'}
            </button>
          ) : null}
          <button type="button" className="vx-btn small" disabled={step === 'working'} onClick={close} data-testid="e2ee-verify-close">
            {step === 'done' || step === 'verified' ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
}
